import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { localiseDigits } from "@/lib/format";
import { ar } from "@/lib/i18n/ar";
import { en, type Catalog, type MessageKey } from "@/lib/i18n/en";
import { fa } from "@/lib/i18n/fa";

export type Locale = "en" | "fa" | "ar";
export type Direction = "ltr" | "rtl";

export const LOCALES: { id: Locale; label: string; dir: Direction }[] = [
  { id: "en", label: "English", dir: "ltr" },
  { id: "fa", label: "فارسی", dir: "rtl" },
  { id: "ar", label: "العربية", dir: "rtl" },
];

const CATALOGS: Record<Locale, Catalog> = { en, fa, ar };

const pluralRules = new Map<Locale, Intl.PluralRules>();

function pluralKey(key: MessageKey, locale: Locale, count: number): string {
  let rules = pluralRules.get(locale);
  if (rules === undefined) {
    rules = new Intl.PluralRules(locale);
    pluralRules.set(locale, rules);
  }
  return `${key}#${rules.select(count)}`;
}
const KEY = "bsmeet.locale";

export function directionOf(locale: Locale): Direction {
  return locale === "en" ? "ltr" : "rtl";
}

function initialLocale(): Locale {
  try {
    const stored = globalThis.localStorage.getItem(KEY);
    if (stored === "en" || stored === "fa" || stored === "ar") return stored;
  } catch (error) {
    console.error("locale preference read failed:", error);
  }
  for (const tag of globalThis.navigator?.languages ?? []) {
    const base = tag.split("-")[0]?.toLowerCase();
    if (base === "fa" || base === "ar" || base === "en") return base;

    if (base === "prs" || base === "ps") return "fa";
  }
  return "en";
}

function fill(template: string, locale: Locale, params?: Record<string, string | number>): string {
  if (params === undefined) return template;
  return template.replaceAll(/\{(\w+)\}/gu, (whole, name: string) => {
    const value = params[name];
    if (value === undefined) return whole;
    return typeof value === "number" ? localiseDigits(String(value), locale) : value;
  });
}

export type { MessageKey };

export type Translate = (key: MessageKey, params?: Record<string, string | number>) => string;

type I18nApi = {
  locale: Locale;
  dir: Direction;
  setLocale: (locale: Locale) => void;
  t: Translate;
};

const I18nContext = createContext<I18nApi | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const dir = directionOf(locale);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("lang", locale);
    root.setAttribute("dir", dir);
  }, [locale, dir]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      globalThis.localStorage.setItem(KEY, next);
    } catch (error) {
      console.error("locale preference save failed:", error);
    }
  }, []);

  const api = useMemo<I18nApi>(() => {
    const catalog = CATALOGS[locale];
    return {
      locale,
      dir,
      setLocale,
      t: (key, params) => {
        const count = params?.["count"];
        if (typeof count === "number") {
          const form = (catalog as Record<string, string | undefined>)[
            pluralKey(key, locale, count)
          ];
          if (form !== undefined) return fill(form, locale, params);
        }
        return fill(catalog[key] ?? en[key], locale, params);
      },
    };
  }, [locale, dir, setLocale]);

  return <I18nContext value={api}>{children}</I18nContext>;
}

export function useI18n(): I18nApi {
  const api = use(I18nContext);
  if (api === null) throw new Error("useI18n used outside I18nProvider");
  return api;
}

export function useT(): Translate {
  return useI18n().t;
}
